'use client';

import type { ReactElement } from 'react';
import { Button, Heading, MediaBlock, TextPassage } from '@petsmart-ui/sparky';
import { isLocaleCA } from '@libs/core';
import { dataLayerPush } from '@libs/analytics';
import { usePathname } from 'next/navigation';
import { OfferCardButton } from './OfferCardButton';
import { useOfferCardActions } from './useOfferCardActions';
import { AlertBannerContainer } from '../ValidationAlert';

export function OfferCardDetails(): ReactElement | null {
    const { handleActivateOffer, selectedOffer, showSpinner } =
        useOfferCardActions();

    const pathname = usePathname();

    if (!selectedOffer) {
        return null;
    }

    const expirationDate = new Date(selectedOffer.availabilityEnd);
    const expirationDateStr = `Exp ${expirationDate.toLocaleDateString()}`;
    const showButton = selectedOffer.isActivatable || selectedOffer.isActivated;
    const shopButtonUrl = isLocaleCA()
        ? selectedOffer.webUrlCa
        : selectedOffer.webUrl;

    return (
        <div className="c-offer-card-details">
            <AlertBannerContainer />

            <MediaBlock>
                <MediaBlock.Header>
                    <img
                        alt={selectedOffer.shortDescription}
                        src={selectedOffer.webImageUrl}
                    />
                </MediaBlock.Header>
                <MediaBlock.Body>
                    <Heading size="body-lg-bold" tagName="h2">
                        {selectedOffer.shortDescription}
                    </Heading>
                    <TextPassage size="sm">
                        <p>{expirationDateStr}</p>
                    </TextPassage>
                </MediaBlock.Body>
            </MediaBlock>
            <TextPassage className="offer-card-details-description">
                <p>{selectedOffer.longDescription}</p>
            </TextPassage>

            {showButton && (
                <OfferCardButton
                    fullWidth
                    isActivated={selectedOffer.isActivated}
                    loading={showSpinner(selectedOffer)}
                    onClick={e => {
                        const element = e.currentTarget as HTMLElement;
                        e.stopPropagation();
                        handleActivateOffer(selectedOffer);
                        element.blur();

                        if (!selectedOffer.isActivated) {
                            const siteSection = pathname?.includes('offers')
                                ? 'offers page'
                                : 'treats rewards';
                            dataLayerPush({
                                event: 'activate_reward',
                                selection: selectedOffer.shortDescription,
                                site_section: siteSection,
                            });
                        }
                    }}
                    size="lg"
                />
            )}
            {shopButtonUrl && (
                <a href={shopButtonUrl}>
                    <Button
                        className="shop-offer-btn"
                        text="Shop this offer"
                        fullWidth
                    />
                </a>
            )}
        </div>
    );
}
